import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { operationsApi } from '@/services/api';
import type { OperationsChecklist } from '@/types/operations';
import { PageFrame, PageHeader, Surface, Note, Placeholder } from '@/components/ui';
import ChecklistView from './ChecklistView';
import { getApiErrorMessage } from './helpers';
import shared from '@/components/utils/shared.module.css';

const ChecklistDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const numId = Number(id ?? '');
  const [checklist, setChecklist] = useState<OperationsChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(numId)) {
      setError('Invalid checklist ID.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await operationsApi.getById(numId);
      setChecklist(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load the checklist.'));
    } finally {
      setLoading(false);
    }
  }, [numId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PageFrame
      header={
        <PageHeader
          title="Checklist"
          subtitle="A past operational checklist and how each step came out."
          actions={
            <Link to="/operations-checklist/history" className={shared['btn-back']}>
              <ArrowLeft size={16} /> Back to history
            </Link>
          }
        />
      }
    >
      {loading && (
        <Surface>
          <Placeholder kind="loading" message="Loading checklist…" />
        </Surface>
      )}

      {!loading && error && <Note tone="error">{error}</Note>}

      {!loading && !error && (
        checklist
          ? checklist.steps.length === 0
            ? (
              <Surface>
                <Placeholder message="This checklist has no steps." />
              </Surface>
            )
            : <ChecklistView checklist={checklist} onChange={setChecklist} />
          : <Note tone="error">Checklist not found.</Note>
      )}
    </PageFrame>
  );
};

export default ChecklistDetailPage;
